import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import React from "react";
import { api } from "../../../../api/api";
import makeStyles from "@mui/styles/makeStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useSnackbar } from "notistack";


const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  tableContainer: {
    maxWidth: 500,
  },
  tablesWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(3),
    maxWidth: 500,
  },
  leftTableContainer: {
    // Fixed width for the left table
    flex: '0 0 300px',
    width: 300,
    minWidth: 300,
    maxWidth: 300,
  },
  rightTableContainer: {
    // Do not grow to fill the screen; width should be defined by its content (years * 100px)
    flex: '0 0 auto',
    overflowX: 'auto',
    maxWidth: '100%',
  },
  // Ensure the same row heights across both tables for seamless continuation
  row: {
    '& > td, & > th': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    }
  },
  // Ensure right table respects fixed column widths without stretching
  rightTable: {
    tableLayout: 'fixed',
    width: 'max-content',
  },
  totalRow: {
    borderBottom: '3px solid rgba(224, 224, 224, 1)',
  },
  countryHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
  },
  historyOnlyRow: {
    color: '#9e9e9e',
    backgroundColor: '#fafafa',
    '& td': {
      color: '#9e9e9e',
    },
  },
  increased: {
    color: '#4caf50',
  },
  decreased: {
    color: '#ef5350',
  },

}));

export function SantaLocationsStatistics(_) {
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();
  const [rawData, setRawData] = React.useState([]);
  const [previousRawData, setPreviousRawData] = React.useState([]);
  const [historyData, setHistoryData] = React.useState([]);
  const [years, setYears] = React.useState([]);
  const [showZeros, setShowZeros] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);

  // Local storage keys and helpers
  const LS_KEY = 'santaLocations:lastSnapshot:v1';

  const loadPreviousSnapshot = React.useCallback(() => {
    try {
      const text = window.localStorage.getItem(LS_KEY);
      if (!text) return [];
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed?.data)) {
        return parsed.data;
      }
      return [];
    } catch (_) {
      return [];
    }
  }, []);

  const saveSnapshot = React.useCallback((data) => {
    try {
      const payload = { timestamp: Date.now(), data };
      window.localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error('Unable to save snapshot', error);
    }
  }, []);

  React.useEffect(() => {
    // Load previous snapshot from localStorage so we can compare even across sessions
    const stored = loadPreviousSnapshot();
    if (stored && stored.length) {
      setPreviousRawData(stored);
    }
    handleReload();
  }, []);

  const handleReload = () => {
    // Prefer persisted snapshot as previous data; fallback to current in-memory data
    const stored = loadPreviousSnapshot();
    if (stored && stored.length) {
      setPreviousRawData(stored);
    } else {
      setPreviousRawData(rawData);
    }

    api.secretSanta.getAllLocations()
      .then((response) => {
        const content = response.content;
        setRawData(content);
        // Persist latest snapshot to localStorage for future sessions
        saveSnapshot(content);
      })
      .catch((error) => {
        snackBarAddMessage(`Error on location load ${error}`, 'error')
      })
  };

  // Load history once on tab load and compute years
  React.useEffect(() => {
    let isMounted = true;
    api.secretSanta.getLocationsHistory()
      .then((response) => {
        if (!isMounted) return;
        const history = response.content || response || [];
        setHistoryData(history);
        const yearsSet = new Set();
        (history || []).forEach(h => {
          yearsSet.add(h.year);
        });
        const yearsArr = Array.from(yearsSet).sort((a, b) => b - a);
        setYears(yearsArr);
      })
      .catch((error) => {
        if (!isMounted) return;
        snackBarAddMessage(`Error on history load ${error.status}`, 'error')
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Compute filtered and grouped data based on rawData and showZeros
  const data = React.useMemo(() => {
    // Create a map of previous data for quick lookup
    const previousMap = new Map();
    previousRawData.forEach(loc => {
      previousMap.set(loc.name, Number(loc.santasCount));
    });

    let locations = rawData.filter(loc => loc.isActive);
    // Filter out zero-count locations if showZeros is false
    if (!showZeros) {
      locations = locations.filter(loc => Number(loc.santasCount) > 0);
    }

    // Build history maps for quick lookup
    const historyByName = new Map(); // name -> { year -> count }
    (historyData || []).forEach(h => {
      if (!historyByName.has(h.name)) historyByName.set(h.name, new Map());
      historyByName.get(h.name).set(h.year, Number(h.santasCount) || 0);
    });

    const currentNames = new Set(locations.map(l => l.name));

    // Group locations by country
    const grouped = [];
    let currentCountry = null;
    let countryGroup = [];

    locations.forEach(loc => {
      const [country, city] = loc.name.split(' > ').map(s => s.trim());

      if (country !== currentCountry) {
        if (countryGroup.length > 0) {
          grouped.push({ type: 'country', country: currentCountry, locations: countryGroup, historyOnly: false });
        }
        currentCountry = country;
        countryGroup = [];
      }

      // Check if the count has changed
      const currentCount = Number(loc.santasCount);
      const previousCount = previousMap.get(loc.name);
      const hasChanged = previousCount !== undefined && previousCount !== currentCount;
      const changeDirection = hasChanged ? (currentCount > previousCount ? 'increased' : 'decreased') : null;

      countryGroup.push({
        ...loc,
        country,
        city,
        previousCount: hasChanged ? previousCount : undefined,
        hasChanged,
        changeDirection,
        history: historyByName.get(loc.name) || new Map(),
      });
    });

    // Add the last group
    if (countryGroup.length > 0) {
      grouped.push({ type: 'country', country: currentCountry, locations: countryGroup, historyOnly: false });
    }

    // Append history-only locations that are not present in current locations
    const historyOnlyNames = new Set();
    historyByName.forEach((_, name) => {
      if (!currentNames.has(name)) historyOnlyNames.add(name);
    });

    // Build and group history-only locations by country at the bottom
    const historyOnlyByCountry = new Map();
    historyOnlyNames.forEach(name => {
      const [country, city] = name.split(' > ').map(s => s.trim());
      // Decide if we include this row depending on showZeros
      let include = true;
      if (!showZeros) {
        // include if any historical count > 0
        const byYear = historyByName.get(name) || new Map();
        include = Array.from(byYear.values()).some(v => Number(v) > 0);
      }
      if (!include) return;

      const rec = {
        id: `history-only-${name}`,
        name,
        country,
        city,
        isActive: false,
        santasCount: 0,
        hasChanged: false,
        previousCount: undefined,
        changeDirection: null,
        history: historyByName.get(name) || new Map(),
        historyOnly: true,
      };
      if (!historyOnlyByCountry.has(country)) historyOnlyByCountry.set(country, []);
      historyOnlyByCountry.get(country).push(rec);
    });

    if (historyOnlyByCountry.size > 0) {
      // Append each country group in alphabetical country order for stability
      Array.from(historyOnlyByCountry.keys()).sort().forEach(country => {
        const list = historyOnlyByCountry.get(country);
        // Sort by city
        list.sort((a, b) => a.city.localeCompare(b.city));
        grouped.push({ type: 'country', country, locations: list, historyOnly: true });
      });
    }

    return grouped;
  }, [rawData, previousRawData, historyData, showZeros]);

  const snackBarAddMessage = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };


  const totalCount = data.reduce((sum, group) => {
    return sum + group.locations.reduce((locSum, loc) => locSum + Number(loc.santasCount || 0), 0);
  }, 0);

  // Totals per historical year
  const totalsByYear = React.useMemo(() => {
    const res = new Map();
    years.forEach(y => res.set(y, 0));
    data.forEach(group => {
      group.locations.forEach(loc => {
        years.forEach(y => {
          const v = (loc.history && loc.history.get) ? Number(loc.history.get(y) || 0) : 0;
          res.set(y, Number(res.get(y) || 0) + v);
        });
      });
    });
    return res;
  }, [data, years]);

  // Calculate previous total count
  const previousTotalCount = previousRawData
    .filter(loc => loc.isActive)
    .reduce((sum, loc) => sum + Number(loc.santasCount), 0);

  // Determine if total has changed
  const totalHasChanged = previousRawData.length > 0 && previousTotalCount !== totalCount;
  const totalChangeDirection = totalHasChanged ? (totalCount > previousTotalCount ? 'increased' : 'decreased') : null;

  return <Paper className={classes.paper}>
    <Typography variant="h6" gutterBottom>
      Statistics
    </Typography>
    <Button
      variant="contained"
      color="primary"
      onClick={handleReload}
      className={classes.button}
    >
      Reload
    </Button>
    <FormControlLabel
      label="Show locations with zeros"
      control={
        <Switch
          checked={showZeros}
          onChange={(e) => setShowZeros(e.target.checked)}
          color="primary"
        />
      }
      className={classes.button}
    />
    <FormControlLabel
      label="Show history"
      control={
        <Switch
          checked={showHistory}
          onChange={(e) => setShowHistory(e.target.checked)}
          color="primary"
        />
      }
      className={classes.button}
    />
    {/*<p></p>*/}
    <div className={classes.tablesWrap}>
      {/* Left table: main data */}
      <TableContainer className={classes.leftTableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow className={classes.row}>
              <TableCell>Location</TableCell>
              <TableCell align="right">Santas Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 && (
              <TableRow className={`${classes.totalRow} ${classes.row}`}>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell
                  align="right"
                  className={totalChangeDirection ? classes[totalChangeDirection] : ''}
                >
                  <strong>
                    {totalHasChanged
                      ? `${previousTotalCount} → ${totalCount}`
                      : totalCount}
                  </strong>
                </TableCell>
              </TableRow>
            )}
            {data.map((group) => (
              (showHistory || !group.historyOnly) &&
              <React.Fragment key={`left-${group.country}-${group.historyOnly}`}>
                <TableRow className={`${classes.countryHeader} ${classes.row}`}>
                  <TableCell colSpan={2}><strong>{group.country}</strong></TableCell>
                </TableRow>
                {group.locations
                  .filter(loc => showHistory || !loc.historyOnly)
                  .map((loc) => (
                    <TableRow key={`left-${loc.name}`}
                              className={`${classes.row} ${loc.historyOnly ? classes.historyOnlyRow : ''}`}>
                      <TableCell style={{ paddingLeft: 32 }}>{loc.city}</TableCell>
                      <TableCell
                        align="right"
                        className={loc.changeDirection ? classes[loc.changeDirection] : ''}
                      >
                        {loc.hasChanged
                          ? `${loc.previousCount} → ${loc.santasCount}`
                          : (loc.historyOnly && Number(loc.santasCount || 0) === 0 ? '' : loc.santasCount)}
                      </TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Right table: history years */}
      {showHistory
        && <TableContainer className={classes.rightTableContainer}>
          <Table size="small" className={classes.rightTable}>
            <TableHead>
              <TableRow className={classes.row}>
                {years.map(y => (
                  <TableCell key={`year-head-${y}`} align="right">{y}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 && (
                <TableRow className={`${classes.totalRow} ${classes.row}`}>
                  {years.map(y => (
                    <TableCell key={`total-${y}`} align="right">
                      <strong>{Number(totalsByYear.get(y) || 0)}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              )}
              {data.map((group) => (
                <React.Fragment key={`right-${group.country}-${group.historyOnly}`}>
                  <TableRow className={`${classes.countryHeader} ${classes.row}`}>
                    <TableCell colSpan={years.length}>{'\u00A0'}</TableCell>
                  </TableRow>
                  {group.locations.map((loc) => (
                    <TableRow key={`right-${loc.name}`}
                              className={`${classes.row} ${loc.historyOnly ? classes.historyOnlyRow : ''}`}>
                      {years.map(y => {
                        const v = (loc.history && loc.history.get) ? loc.history.get(y) : -1;
                        const display = v > 0 ? v : '\u00A0';
                        return (
                          <TableCell key={`right-${loc.name}-${y}`} align="right">{display}</TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
    </div>


  </Paper>;
}
